import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  PRIVACY_INTRO,
  PRIVACY_SECTIONS,
  TERMS_INTRO,
  TERMS_SECTIONS,
} from '../lib/legalDocuments';
import { REFUND_POLICY_INTRO, REFUND_POLICY_ITEMS } from '../lib/refundPolicy';

function SectionBlock({ section }) {
  return (
    <section className="space-y-3">
      <h4 className="text-sm font-bold text-white">{section.title}</h4>
      {(section.paragraphs || []).map((paragraph) => (
        <p key={paragraph} className="text-sm leading-relaxed text-gray-300">
          {paragraph}
        </p>
      ))}
      {Array.isArray(section.bullets) && section.bullets.length > 0 ? (
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-gray-300">
          {section.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {(section.afterBullets || []).map((paragraph) => (
        <p key={paragraph} className="text-sm leading-relaxed text-gray-300">
          {paragraph}
        </p>
      ))}
    </section>
  );
}

function AccordionPanel({ id, title, subtitle, open, onToggle, children }) {
  return (
    <div className="rounded-md border border-white/10 bg-[#1a1a1a] overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-3 px-4 py-4 text-left hover:bg-white/[0.03] transition"
      >
        <span>
          <span className="block text-sm font-semibold text-white">{title}</span>
          {subtitle ? (
            <span className="mt-1 block text-xs leading-relaxed text-gray-400">{subtitle}</span>
          ) : null}
        </span>
        <ChevronDown
          size={18}
          className={`mt-0.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div className="border-t border-white/10 px-4 py-4 space-y-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export default function TermsPolicyContent({
  showRefundPolicy = false,
  onSupportClick,
  supportLinkLabel = 'Supports Page',
}) {
  const [openPanel, setOpenPanel] = useState('');

  const togglePanel = (id) => {
    setOpenPanel((prev) => (prev === id ? '' : id));
  };

  return (
    <div className="space-y-4">
      <AccordionPanel
        id="terms"
        title="Terms of Use"
        subtitle="Rules for using Echo Jham"
        open={openPanel === 'terms'}
        onToggle={togglePanel}
      >
        <p className="text-sm leading-relaxed text-gray-300">{TERMS_INTRO}</p>
        {TERMS_SECTIONS.map((section) => (
          <SectionBlock key={section.id} section={section} />
        ))}
      </AccordionPanel>

      <AccordionPanel
        id="privacy"
        title="Privacy Policy"
        subtitle="How we handle your information"
        open={openPanel === 'privacy'}
        onToggle={togglePanel}
      >
        <p className="text-sm leading-relaxed text-gray-300">{PRIVACY_INTRO}</p>
        {PRIVACY_SECTIONS.map((section) => (
          <SectionBlock key={section.id} section={section} />
        ))}
      </AccordionPanel>

      {showRefundPolicy ? (
        <AccordionPanel
          id="refund"
          title="Refund Policy"
          subtitle={REFUND_POLICY_INTRO}
          open={openPanel === 'refund'}
          onToggle={togglePanel}
        >
          <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-gray-300">
            {REFUND_POLICY_ITEMS.map((item) => (
              <li key={item.title}>
                <span className="font-semibold text-white">{item.title}</span>
                {' — '}
                {item.body}
              </li>
            ))}
          </ol>
        </AccordionPanel>
      ) : null}

      <div className="rounded-md border border-white/10 bg-[#1a1a1a] px-4 py-5 space-y-2">
        <h3 className="text-sm font-semibold text-white">Need help?</h3>
        <p className="text-sm leading-relaxed text-gray-300">
          If you have questions about your privacy, account, or use of our services, contact our support team.
        </p>
        <p className="text-sm leading-relaxed text-gray-300">
          Contact Support via the{' '}
          {typeof onSupportClick === 'function' ? (
            <button
              type="button"
              onClick={onSupportClick}
              className="font-semibold text-brand-maroon hover:underline"
            >
              {supportLinkLabel}
            </button>
          ) : (
            <span className="font-semibold text-white">{supportLinkLabel}</span>
          )}{' '}
          inside the app or contact us directly through email{' '}
          <a
            href="mailto:echojhamproductions@gmail.com"
            className="font-semibold text-brand-maroon hover:underline break-all"
          >
            echojhamproductions@gmail.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}
